import { useEffect, useState } from 'react';
import {
  countryName,
  playerDisplayName,
  type AuthResult,
  type PlayerStats,
} from '@aiscoreboard/contracts';
import type { CSSProperties } from 'react';
import { CountryPicker, Flag } from './CountryPicker';
import { PairPanel } from './PairPanel';
import { Avatar } from '../screens/ProfileScreen';
import { fetchMyStats } from '../lib/accounts';
import type { Accent } from '../lib/accent';
import { useT } from '../lib/i18n';
import type { SeatSession } from '../lib/useSeats';

interface Props {
  label: string;
  session: SeatSession | null;
  /** Guest name used when nobody signs in. Omitted for practice. */
  guestName?: string;
  onGuestNameChange?: (name: string) => void;
  guestCountry?: string;
  onGuestCountryChange?: (country: string) => void;
  onSignIn: () => void;
  onSignOut: () => void;
  onSettings: () => void;
  /**
   * The colour this seat becomes once the match starts.
   *
   * A seat card is the same box as the player panel it turns into — same
   * width, same gaps, same place on the screen — and it now carries the same
   * colour, so the change from setting up to scoring reads as the match
   * starting rather than as a different page loading. Neither seat is dimmed
   * here: before the first break there is no player at the table, and dimming
   * one of two identical cards would say there is.
   */
  accent?: Accent;
  /**
   * When given, an empty seat shows its own pairing QR code instead of only a
   * button. Each seat needs a separate code — one code can only be claimed
   * once, so a shared one would seat whoever scanned first and leave the
   * other player wondering why nothing happened.
   */
  onPaired?: (auth: AuthResult) => void;
  /** Side of the pairing code in pixels, for a card narrower than the match's. */
  codeSize?: number;
  /**
   * Something the seat has to say before anybody sits in it — today, that the
   * account just offered is already in the other seat. It belongs on the card
   * rather than in a corner of the screen because the card is what the player
   * was looking at when they were refused.
   */
  notice?: string | null;
}

/**
 * Side of the portrait, and of the pairing code, when the card does not say.
 * One number for both so that signing in swaps a square for a square of
 * exactly the same size and nothing on the card moves.
 */
const DEFAULT_PORTRAIT = 251;

export function SeatCard({
  label,
  session,
  guestName,
  onGuestNameChange,
  guestCountry,
  onGuestCountryChange,
  onSignIn,
  onSignOut,
  onSettings,
  onPaired,
  accent,
  codeSize,
  notice,
}: Props) {
  const t = useT();

  // Softer than the scoring board's live panel: two cards lit as brightly as
  // the one at the table would be two things shouting at each other.
  const tint = accent
    ? ({
        '--hud-bright': `rgba(${accent.rgb},0.34)`,
        '--hud-glow': `rgba(${accent.rgb},0.08)`,
        '--hud-fill': `rgba(${accent.rgb},0.05)`,
      } as CSSProperties)
    : undefined;
  const seatLabel = accent ? ({ color: accent.hex } as CSSProperties) : undefined;

  if (!session) {
    return (
      <div
        className="seat-card seat-card-guest hud-frame flex min-w-0 flex-1 flex-col gap-4 p-5"
        style={tint}
      >
        <span className="label mb-0 shrink-0" style={seatLabel}>
          {label}
        </span>

        {/* Auto margins spend spare height without centring overflowing
            content above the start of the page's scrolling surface. */}
        <div className="flex flex-1 flex-col gap-4">
          {onPaired && (
            <div className="seat-media my-auto flex shrink-0 flex-col items-center">
              <PairPanel
                onDone={onPaired}
                compact
                compactSize={codeSize}
                caption={t('pair.forSeat', { seat: label })}
              />
            </div>
          )}

          <button type="button" onClick={onSignIn} className="btn shrink-0 bg-white text-hud-bg">
            {t('common.signIn')}
          </button>

          {notice && (
            <p className="shrink-0 rounded-xl bg-ball-red/15 px-3 py-2 text-center text-sm font-bold text-ball-red">
              {notice}
            </p>
          )}

          {onGuestNameChange && (
            <div className="seat-guest my-auto flex shrink-0 flex-col">
              <span className="label">{t('seat.guestName')}</span>
              <input
                className="field"
                value={guestName ?? ''}
                onChange={(e) => onGuestNameChange(e.target.value)}
              />
              {onGuestCountryChange && (
                <div className="mt-2">
                  <CountryPicker
                    value={guestCountry ?? 'DE'}
                    onChange={onGuestCountryChange}
                    label={t('board.country')}
                    showAllOnOpen
                    autoFocusOnOpen={false}
                    openUp
                  />
                </div>
              )}
              <p className="seat-hint mt-1 text-xs text-white/40">{t('seat.guestHint')}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const { player } = session;
  return (
    <div className="seat-card seat-card-auth hud-frame flex min-w-0 flex-1 flex-col gap-2 p-5" style={tint}>
      <span className="label mb-0 shrink-0" style={seatLabel}>
        {label}
      </span>

      {/* The same three bands as the signed-out card, so signing in does not
          move anything: the portrait takes the square the pairing code had,
          the record sits where the sign-in button was, and the two buttons
          take the guest field's place at the foot of the card.

          One page scrolling surface, not nested seat scrollers. Every band
          is sized by its contents; the identity takes any
          spare height with an auto margin, which collapses to nothing rather
          than pushing anything off the top when there is none. */}
      <div className="flex flex-1 flex-col gap-2">
        <div className="my-auto flex w-full shrink-0 flex-col items-center gap-1">
          <div className="seat-square" style={{ '--seat-code-max': `${codeSize ?? DEFAULT_PORTRAIT}px` } as CSSProperties}>
            <Avatar player={player} size="100%" />
          </div>
          <div className="w-full min-w-0 text-center">
            <p className="truncate text-xl font-bold sm:text-2xl">{playerDisplayName(player)}</p>
            <p className="mt-1 flex items-center justify-center gap-2 text-sm text-white/55">
              <Flag code={player.country} />
              <span className="truncate">{player.club?.name ?? countryName(player.country)}</span>
            </p>
            {/* The login number gets a line of its own under the club rather
                than a third item on that line, and it is named. It is the
                one thing on the card a player reads out to somebody else,
                and hunting for it at the end of a row that may already have
                been truncated is the opposite of that; a lone run of digits
                in a card that also shows a win rate and a highest break
                needs to say which number it is. */}
            <p className="mt-1 flex items-baseline justify-center gap-1.5">
              <span className="text-xs uppercase tracking-widest text-white/40">
                {t('seat.idLabel')}
              </span>
              <span className="font-mono text-sm tracking-widest text-sys-yellow">
                {player.publicId}
              </span>
            </p>
          </div>
        </div>

        <SeatStats token={session.token} />

        {/* Equal halves: two buttons of different widths read as a main action
            and an afterthought, and neither of these is. Signing out is the
            one thing here that undoes something, so it is the only red. */}
        <div className="grid shrink-0 grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onSettings}
            className="btn-ghost min-h-11 min-w-0 px-2 py-2 text-sm"
          >
            {t('common.settings')}
          </button>
          <button
            type="button"
            onClick={onSignOut}
            className="btn-ghost min-h-11 min-w-0 bg-ball-red/10 px-2 py-2 text-sm text-ball-red
                       ring-ball-red/40 hover:bg-ball-red/20 hover:ring-ball-red/60"
          >
            {t('common.signOut')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The player's record, in the middle of their seat card.
 *
 * Five numbers, no more: which of two players is the stronger is a question
 * the card should answer at a glance, and a table of fifteen figures answers
 * it slower than none at all. The rest is on the profile screen.
 *
 * Fetched per seat with that seat's own token, because these are the signed-in
 * player's private totals and the board has no other way to ask for them.
 */
function SeatStats({ token }: { token: string }) {
  const t = useT();
  const [stats, setStats] = useState<PlayerStats | null>(null);

  useEffect(() => {
    let live = true;
    setStats(null);
    fetchMyStats(token)
      .then((res) => {
        if (live) setStats(res.stats);
      })
      // A record that will not load must not take the seat down with it: the
      // player is still signed in and the match can still be started.
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [token]);

  const pct = (n: number): string => `${Math.round(n * 100)}%`;
  // Ceilings a club player can read without being told what they are: a rate
  // is out of one, a hundred break is the number every player is chasing, and
  // fifty a frame is a very good average. The last two are ceilings rather
  // than limits — 147 and a 60 average both exist, and the bar simply fills
  // and says so, because a scale that had to fit a maximum break would leave
  // every real record sitting in the first fifth of it.
  const bars: Array<{ term: string; value: string; fill: number; over: boolean }> = [
    bar(t('seat.statWinRate'), stats && pct(stats.matchWinRate), stats?.matchWinRate, 1),
    bar(t('seat.statPotRate'), stats && pct(stats.potSuccessRate), stats?.potSuccessRate, 1),
    bar(t('seat.statHighBreak'), stats && String(stats.highestBreak), stats?.highestBreak, 100),
    bar(t('seat.statAvgBreak'), stats && stats.averageBreak.toFixed(1), stats?.averageBreak, 50),
  ];

  return (
    <div className="w-full shrink-0">
      {/* A hairline above the record, so the rows read as a block of their
          own rather than as more lines of the identity above them — the club,
          the login number and "Matches 42" are all one column of centred
          small text otherwise, and they run together. */}
      <hr className="mx-auto mb-2 w-full max-w-[20rem] border-0 border-t border-white/10" />
      {/* Held to a readable measure and centred rather than spread across
          the whole card: a label at one edge and its number at the other is
          a pair the eye has to travel to reconnect, and this card is as
          wide as the player panel it becomes. */}
      <dl className="mx-auto flex w-full max-w-[20rem] flex-col gap-1">
        {/* Frames played gets no bar. It is a count, not a standard: two
            hundred frames is not "better" than fifty in the way that 70%
            potting is better than 50%, and a bar under it would invite
            exactly that reading. */}
        <div className="flex items-baseline justify-between gap-3">
          <dt className="min-w-0 truncate text-[0.7rem] uppercase tracking-wide text-white/45">
            {t('seat.statFrames')}
          </dt>
          <dd className="num shrink-0 text-sm tabular-nums">
            {stats ? String(stats.framesPlayed) : '—'}
          </dd>
        </div>

        {bars.map((row) => (
          <div key={row.term}>
            <div className="flex items-baseline justify-between gap-3">
              <dt className="min-w-0 truncate text-[0.7rem] uppercase tracking-wide text-white/45">
                {row.term}
              </dt>
              <dd className="num shrink-0 text-sm tabular-nums">{row.value}</dd>
            </div>
            {/* Every bar is the same length whatever it measures, because the
                thing being compared is the two cards' rows against each
                other. A full bar that is *over* its ceiling is lit rather
                than merely full, so a 147 does not read as the same
                achievement as a hundred. */}
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full transition-[width]"
                style={{
                  width: `${row.fill * 100}%`,
                  background: row.over ? '#ffe45e' : '#ffd60a',
                  boxShadow: row.over ? '0 0 8px rgba(255, 214, 10, 0.65)' : undefined,
                }}
              />
            </div>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** One row of the record: its figure, and how full its bar should be. */
function bar(
  term: string,
  value: string | null | undefined | false,
  raw: number | undefined,
  ceiling: number,
): { term: string; value: string; fill: number; over: boolean } {
  const n = raw ?? 0;
  return {
    term,
    value: value ? value : '—',
    fill: Math.max(0, Math.min(1, n / ceiling)),
    over: n > ceiling,
  };
}
